import { useState } from "react";
import { motion } from "framer-motion";
import { Check, BookOpen, Dumbbell, Sparkles } from "lucide-react";
import { MorningStep } from "./day/MorningStep";
import { NoonStep } from "./day/NoonStep";
import { EveningStep } from "./day/EveningStep";

function DoneButton({ done, saving, onClick, testid, label = "Segna come fatto", doneLabel = "Completato · tocca per annullare" }) {
  return (
    <button
      onClick={onClick}
      disabled={saving}
      data-testid={testid}
      className={`mt-4 flex w-full items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-semibold uppercase tracking-wide transition ${done ?"border border-[#10B981]/40 bg-[#10B981]/10 text-[#10B981]" : "btn-gold"}`}
    >
      {done ? (<><Check size={16} /> {doneLabel}</>) : (saving ? "…" : label)}
    </button>
  );
}

export function DayContent({ payload, options = [], progress, times, saving, onToggle, onInteract }) {
  const [showLettura, setShowLettura] = useState(false);
  const [showExtra, setShowExtra] = useState(false);

  return (
    <div className="space-y-3">
      <MorningStep data={payload.mattino} day={payload.day} options={options} plan={progress.plan} time={times?.mattino} onSave={(d) => onInteract("mattino", d)} />
      <NoonStep data={payload.focus} plan={progress.plan} checkin={progress.checkin} evening={progress.evening} time={times?.focus} onSave={(d) => onInteract("focus", d)} />
      <EveningStep data={payload.sera} domanda={payload.domanda} plan={progress.plan} checkin={progress.checkin} evening={progress.evening} status={progress.action_status} done={!!progress.sera} time={times?.sera} onSave={(d) => onInteract("sera", d)} />

      {(payload.lettura || payload.esercizio) && (
        <button onClick={() => setShowExtra(!showExtra)} data-testid="extra-toggle" className="flex w-full items-center justify-between rounded-2xl border border-dashed border-[#3F3F46] px-5 py-4 text-left">
          <span>
            <span className="block text-[12px] font-semibold uppercase tracking-wider text-[#8A8E96]">Approfondimento facoltativo</span>
            <span className="text-base text-[#D4D4D8]">{[payload.lettura && "Lettura", payload.esercizio && "Esercizio"].filter(Boolean).join(" e ")}: non serve per completare la giornata</span>
          </span>
          <span className="text-sm text-[#D4AF37]">{showExtra ? "Chiudi" : "Apri"}</span>
        </button>
      )}

      {showExtra && payload.lettura && (
        <div className="rounded-2xl border border-[#27272A] bg-[#121215] p-5" data-testid="lettura-card">
          <button onClick={() => setShowLettura(!showLettura)} className="flex w-full items-center gap-2 text-left">
            <BookOpen size={18} className="text-[#D4AF37]" />
            <span className="flex-1 text-lg text-[#F4F4F5] font-semibold">{payload.lettura.titolo} <span className="text-[12px] font-normal uppercase tracking-wider text-[#8A8E96]">· facoltativa</span></span>
            <span className="text-sm text-[#D4AF37]">{showLettura ? "Chiudi" : "Leggi"}</span>
          </button>
          {showLettura && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-3 space-y-3">
              {payload.lettura.paragrafi.map((p, i) => (
                <p key={i} className="a2g-read text-base leading-relaxed text-[#B4B4BC]">{p}</p>
              ))}
              {payload.lettura.domanda && <p className="a2g-read text-lg text-[#F4F4F5] font-semibold">{payload.lettura.domanda}</p>}
            </motion.div>
          )}
          <DoneButton done={!!progress.lettura} saving={saving === "lettura"} onClick={() => onToggle("lettura")} testid="lettura-toggle" label="Segna la lettura come fatta" doneLabel="Lettura completata · tocca per annullare" />
        </div>
      )}

      {showExtra && payload.esercizio && (
        <div className="rounded-2xl border border-[#D4AF37]/30 bg-[#121215] p-5" data-testid="esercizio-card">
          <div className="flex items-center gap-2">
            <Dumbbell size={18} className="text-[#D4AF37]" />
            <span className="text-[12px] font-semibold uppercase tracking-wider text-[#D4AF37]">Esercizio della settimana · facoltativo</span>
          </div>
          <h3 className="a2g-read-title mt-2 text-xl text-[#F4F4F5] font-semibold">{payload.esercizio.titolo}</h3>
          <div className="mt-3 space-y-1 a2g-read text-base text-[#B4B4BC]">
            <p><b className="text-[#D4D4D8]">Obiettivo:</b> {payload.esercizio.obiettivo}</p>
            <p><b className="text-[#D4D4D8]">Tempo:</b> {payload.esercizio.tempo}</p>
            <p><b className="text-[#D4D4D8]">Occorrente:</b> {payload.esercizio.occorrente}</p>
          </div>
          <ol className="mt-3 space-y-2">
            {payload.esercizio.passi.map((p, i) => (
              <li key={i} className="flex gap-2 a2g-read text-base text-[#D4D4D8]">
                <span className="tabular-nums font-semibold flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#18181C] text-sm font-semibold text-[#D4AF37]">{i + 1}</span>
                <span className="pt-0.5">{p}</span>
              </li>
            ))}
          </ol>
          {payload.esercizio.scelta && (
            <div className="mt-3 flex items-start gap-2 rounded-xl border border-[#27272A] bg-[#0D0D0F] p-3.5">
              <Sparkles size={16} className="mt-0.5 shrink-0 text-[#D4AF37]" />
              <p className="a2g-read text-base font-medium italic text-[#F4F4F5]">{payload.esercizio.scelta}</p>
            </div>
          )}
          <DoneButton done={!!progress.esercizio} saving={saving === "esercizio"} onClick={() => onToggle("esercizio")} testid="esercizio-toggle" label="Segna l'esercizio come fatto" doneLabel="Esercizio completato · tocca per annullare" />
        </div>
      )}
    </div>
  );
}
